import { Button, LoadingSpinner } from "@newsela/angelou";
import { objectTypes, withTrackEvent } from "@newsela/snitch";

import { useEffect } from "react";
import { useAdminContentSettingsContext } from "@/search/AdminContentSettingsContext/AdminContentSettingsContext";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { ContentCard } from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCard";
import { NoResults } from "@/search/NewSearchPageMVP/SearchResults/NoResults/NoResults.component";
import {
  CONTENT_TYPE_STANDARD,
  FIRST_SEARCH_COMPLETED_EVENT,
} from "@/search/NewSearchPageMVP/SearchResults/SearchResults.contants";
import { StandardCard } from "@/search/NewSearchPageMVP/SearchResults/StandardCard/StandardCard";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";
import useGetUserFeatures from "@/users/v3/useUserFeaturesQuery";
import useEventRecordMutation from "@/users/useEventRecordMutation/useEventRecordMutation";
import { getLicenseTypeFromList } from "@/onboarding/onboarding.utils";
import { AgathaSchemaParamsEnum } from "../types/IParametersSchema";
import {
  loadMoreButtonCss,
  resultsContainer,
  searchResultsContainerCss,
} from "./SearchResults.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

const SearchResults = () => {
  const {
    getters: { searchResponse, searchResults, isSearchLoading },
    handlers: { setSearchParamValue, getSearchParamValues },
  } = useSearchPageContext();
  const { postEventRecord } = useEventRecordMutation();

  const { enableContentSettingsFeatures, contentSettingsGroups } =
    useAdminContentSettingsContext();

  const { data: userFeatures } = useGetUserFeatures();

  // Add record to confirm the user's first assignment is complete.
  useEffect(() => {
    if (userFeatures?.includes("needs_first_content_search")) {
      const licenseType = getLicenseTypeFromList(userFeatures);
      postEventRecord({
        event_name: FIRST_SEARCH_COMPLETED_EVENT,
        data: {
          license_type: licenseType,
        },
      });
    }
  }, [userFeatures]);

  if (!searchResults.length && !isSearchLoading) return <NoResults />;

  const totalResultsAmount =
    searchResponse?.total_result_count.value || searchResults.length;
  const showLoadMoreButton =
    !isSearchLoading && totalResultsAmount > searchResults.length;

  return (
    <div css={resultsContainer}>
      <div css={searchResultsContainerCss}>
        <BookmarkContextProvider>
          {searchResults.map((content, index) => {
            if (content.content_type === CONTENT_TYPE_STANDARD) {
              return <StandardCard key={index} content={content} />;
            }
            return (
              <ContentCard
                key={index}
                content={content}
                contentSettingsGroups={
                  enableContentSettingsFeatures ? contentSettingsGroups : []
                }
              />
            );
          })}
        </BookmarkContextProvider>
      </div>

      {isSearchLoading && <LoadingSpinner />}

      {showLoadMoreButton && (
        <ButtonWithTrackEvent
          objectType={objectTypes.user}
          streamProperties={{
            content_type: "Search",
          }}
          __cssFor={loadMoreButtonCss}
          onClick={() => {
            const [currentPage] = getSearchParamValues(
              AgathaSchemaParamsEnum.page,
            );
            setSearchParamValue(
              AgathaSchemaParamsEnum.page,
              `${Number(currentPage) + 1}`,
            );
          }}
          dataProps={{ "data-qa-selector": "search-view-more" }}
        >
          View more results
        </ButtonWithTrackEvent>
      )}
    </div>
  );
};

SearchResults.displayName = "SearchResults";
export { SearchResults };
